import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";

const API_URL = import.meta.env.VITE_API_URL;

// All shipment change request types
export type ShipmentChangeRequestType =
  | "RECIPIENT_ADDRESS_CORRECTION"
  | "RECIPIENT_CONTACT_UPDATE"
  | "RECIPIENT_DELIVERY_INSTRUCTIONS"
  | "DELIVERY_RESCHEDULE"
  | "SENDER_ADDRESS_CORRECTION"
  | "SENDER_CONTACT_UPDATE"
  | "SENDER_PICKUP_INSTRUCTIONS"
  | "PICKUP_RESCHEDULE";

export type ShipmentChangeRequestStatus =
  | "PENDING"
  | "ACKNOWLEDGED"
  | "ACTIONED"
  | "CANNOT_FULFILL"
  | "AUTO_APPROVED";

export type ShipmentPackageCandidate = {
  id: string;
  index: number;
  kind: string | null;
  weightGrams: number;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  weightOriginal: string | null;
  weightOriginalUnit: string | null;
  lengthOriginal: string | null;
  widthOriginal: string | null;
  heightOriginal: string | null;
  dimensionOriginalUnit: string | null;
  description: string | null;
  declaredValueCents: number | null;
  isFragile: boolean;
  requiresCooling: boolean;
  packageTypeId: string | null;
};

export interface AddressChangeProposal {
  packageCandidate?: ShipmentPackageCandidate[] | null;
  parties?: Array<{
    type: string;
    address: ShipmentAddressCandidate | null;
    name: string | null;
    company: string | null;
    phone: string | null;
    email: string | null;
    instructions: string | null;
  }>;
  state: string;
  fingerprint: string;
  commercialDeltaCents: number | null;
  commercialTaxCents: number | null;
  commercialReason: string | null;
  changes: Array<{
    addressType: string;
    before: ShipmentAddressCandidate | null;
    after: ShipmentAddressCandidate;
  }>;
  offer: {
    currency: string;
    pickupPackageQuote: {
      currency: "CAD";
      oldNetCents: number;
      oldTaxCents: number;
      newNetCents: number;
      newTaxCents: number;
      deltaCents: number;
    } | null;
    deliveredPrecision: "DATE" | "DATETIME";
    pickupVisit?: {
      before: ShipmentAddressCandidate | null;
      after: ShipmentAddressCandidate;
      scheduledDate: string;
      slot: { name: string; startTime: string; endTime: string };
      service: { name: string };
      contactName: string;
      contactPhone: string | null;
      contactEmail: string | null;
      instructions: string | null;
      oldGrossCents: number;
      newGrossCents: number;
    } | null;
    service: { name: string; code: string };
    deliveredFastest: string;
    deliveredSlowest: string;
    expiresAt: string;
    readyAt: string | null;
    pickupDate: string | null;
    handoffBy: string | null;
    timeZone: string;
    dispatch: ShipmentAddressCandidate | null;
    dropoff: ShipmentAddressCandidate | null;
  } | null;
}

export interface ShipmentChangeRequest {
  addressChange?: AddressChangeProposal | null;
  id: string;
  status: ShipmentChangeRequestStatus;
  submittedByActorType?: "CUSTOMER" | "CARRIER" | "ADMIN";
  requestTypes: Array<
    ShipmentChangeRequestType | "PACKAGE_DECLARATION_AMENDMENT"
  >;
  rescheduleDate?: string;
  reschedulePreference?: string;
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
  adminResponse?: string;
  appliedChanges?: string[];
  createdAt: string;
  handledAt?: string;
}

export interface ShipmentAddressCandidate {
  line1: string;
  line2: string | null;
  city: string;
  province: string;
  country: string;
  postalCode: string | null;
  plusCode: string | null;
  location: { lat: number; lng: number } | null;
}

export interface CreateShipmentChangeRequestInput {
  addressCandidate?: ShipmentAddressCandidate;
  pickupVisitAddressCandidate?: ShipmentAddressCandidate;
  requestTypes: ShipmentChangeRequestType[];
  rescheduleDate?: string;
  reschedulePreference?: "morning" | "afternoon" | "evening";
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
}

interface CreateShipmentChangeRequestResponse {
  success: boolean;
  message: string;
  request: {
    id: string;
    status: ShipmentChangeRequestStatus;
    requestTypes: ShipmentChangeRequestType[];
    createdAt: string;
  };
  autoApproved: boolean;
  appliedChanges: string[];
}

interface GetShipmentChangeRequestsResponse {
  requests: ShipmentChangeRequest[];
}

/**
 * Submit a change request for a shipment
 */
export function useCreateShipmentChangeRequest(options?: {
  onSuccess?: (data: CreateShipmentChangeRequestResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      ...input
    }: CreateShipmentChangeRequestInput & { shipmentId: string }) => {
      const { data } = await axios.post<CreateShipmentChangeRequestResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/change-requests`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["shipmentHistory"] });
      await queryClient.invalidateQueries({ queryKey: ["tracking"] });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["changeRequests"],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

/**
 * Get change requests for a shipment
 */
export function useShipmentChangeRequests(shipmentId: string | undefined) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["changeRequests", shipmentId],
    queryFn: async () => {
      if (!shipmentId) throw new Error("Shipment ID required");

      const { data } = await axios.get<GetShipmentChangeRequestsResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/change-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data.requests;
    },
    enabled: !!shipmentId && !!token,
    refetchInterval: 10000,
  });
}

/**
 * Format request type for display
 */
export function formatRequestType(type: ShipmentChangeRequestType): string {
  const labels: Record<ShipmentChangeRequestType, string> = {
    RECIPIENT_ADDRESS_CORRECTION: "Recipient address correction",
    RECIPIENT_CONTACT_UPDATE: "Update recipient contact",
    RECIPIENT_DELIVERY_INSTRUCTIONS: "Delivery instructions",
    DELIVERY_RESCHEDULE: "Reschedule delivery",
    SENDER_ADDRESS_CORRECTION: "Sender address correction",
    SENDER_CONTACT_UPDATE: "Update sender contact",
    SENDER_PICKUP_INSTRUCTIONS: "Pickup instructions",
    PICKUP_RESCHEDULE: "Reschedule pickup",
  };
  return labels[type] || type;
}

/**
 * Format status for display
 */
export function formatRequestStatus(
  status: ShipmentChangeRequestStatus
): string {
  const labels: Record<ShipmentChangeRequestStatus, string> = {
    PENDING: "Pending review",
    ACKNOWLEDGED: "Acknowledged",
    ACTIONED: "Handled",
    CANNOT_FULFILL: "Cannot fulfill",
    AUTO_APPROVED: "Applied",
  };
  return labels[status] || status;
}

export function useAcceptAddressChange() {
  const { token, customer } = useSession();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      shipmentId: string;
      requestId: string;
      fingerprint: string;
    }) => {
      const { data } = await axios.post<{ accepted: true; state: string }>(
        `${API_URL}/api/v2/shipments/${input.shipmentId}/change-requests/${input.requestId}/accept-commercial`,
        { fingerprint: input.fingerprint },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["shipmentHistory"] });
      await client.invalidateQueries({ queryKey: ["tracking"] });
      await client.invalidateQueries({ queryKey: ["changeRequests"] });
      await client.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
    },
    onError: async () => {
      await client.invalidateQueries({ queryKey: ["changeRequests"] });
    },
  });
}
